import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useComplianceProfileApi from './config';
import { useAuth } from '../../context/AuthContext';
import { parseTableParams } from '../../utils/Utils';
import DataTable from '../../components/ui/DataTable';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

// Uyum Profilleri (AKM POC §6.3) — TitlesPage / EmployeeStatusesPage aynası. Başlık yalnız DataTable
// `title`'ında; "Yeni" tablonun içinde. Satır tıklaması profilin atama kuralları sayfasını açar.
const Container = styled.div``;

const InfoNote = styled.div`
  background: #fff7ed; border: 1px solid #fed7aa; color: #9a3412;
  border-radius: 6px; padding: 10px 12px; font-size: 13px; margin: 0 0 12px; line-height: 1.4;
`;

const EMPTY_CONFIRM = { isOpen: false, id: null, name: '' };

export default function ComplianceProfilesPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const { canWrite } = useAuth();
  const writable = canWrite('SM');

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(EMPTY_CONFIRM);
  const [deleting, setDeleting] = useState(false);

  const {
    objectSchema, fetching, loadFailed, columns: baseColumns, data, pagination, create, update, _delete,
  } = useComplianceProfileApi(queryParams);

  const handleNewButtonClick = useCallback(() => {
    setSelectedRow(null);
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
  }, []);

  const handleOpenRules = useCallback((row) => {
    history.push(`/compliance-profiles/${row.id}`);
  }, [history]);

  const handleSave = useCallback(async (formData) => {
    const success = selectedRow
      ? await update(selectedRow.id, formData)
      : await create(formData);
    if (success) setDrawerOpen(false);
    return success;
  }, [selectedRow, create, update]);

  const handleDelete = useCallback((row) => {
    setDeleteConfirm({ isOpen: true, id: row.id, name: row.name });
  }, []);

  // Silme reddedilirse (409 complianceProfile.inUse — toast interceptor'da) onay penceresi AÇIK kalır
  // ve tablo yeniden yüklenmez: kullanıcı nedeni okuyup kendisi kapatır.
  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    const ok = await _delete(deleteConfirm.id);
    setDeleting(false);
    if (ok) setDeleteConfirm(EMPTY_CONFIRM);
  }, [_delete, deleteConfirm.id]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    setQueryParams(parseTableParams(tableState, baseColumns));
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => {
    const rules = { type: 'details', label: t('complianceProfile.assignmentRules'), onClick: handleOpenRules };
    if (!writable) return [rules];
    return [
      { type: 'edit', label: t('complianceProfile.edit'), onClick: handleEdit },
      rules,
      { type: 'divider' },
      { type: 'delete', label: t('complianceProfile.delete'), onClick: handleDelete },
    ];
  }, [t, writable, handleEdit, handleDelete, handleOpenRules]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  return (
    <Container>
      <GenericFormDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={selectedRow ? t('complianceProfile.edit') : t('complianceProfile.new')}
        schema={objectSchema}
        initialValues={selectedRow}
        onSave={handleSave}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('complianceProfile.delete')}
        message={t('complianceProfile.deleteMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm(EMPTY_CONFIRM)}
        loading={deleting}
      />

      {loadFailed && (
        <InfoNote role="alert" data-testid="compliance-profile-load-failed">{t('complianceProfile.loadFailed')}</InfoNote>
      )}

      {useMemo(() => (
        <DataTable
          title={t('complianceProfile.title')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          canCreateNewRecord={writable}
          newButtonLabel={t('complianceProfile.new')}
          onNewButtonClick={handleNewButtonClick}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleOpenRules}
        />
      ), [
        t, fetching, columns, data, pagination, writable,
        handleNewButtonClick, filterList, handleFilterConfirm, handleDataStateChange, handleOpenRules,
      ])}
    </Container>
  );
}
